import { useEffect, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import {
  getOauthApplication,
  oauthApplicationsQueryKeys,
} from '../../api/oauthApplications';
import type { OauthApplicationShowResponse } from '../../api/oauthApplications';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect.
import type OAuthAppManagement from '../../components/OAuthAppManagement/index';
import type OAuthAppDetails from '../../components/OAuthAppDetails/index';
import type EditOAuthApp from '../../components/EditOAuthApp/index';
import type OAuthScopeRequests from '../../components/OAuthScopeRequests/index';

export const OAUTH_STALE_TIME_MS = 30_000;

// The mount views ship the full show payload (app + scope requests + audit
// events) as initialOauthPayload, so a server-rendered entry renders complete
// sections without a refetch flash.
export function useOauthApplicationRaw(
  idNum: number,
  seed: OauthApplicationShowResponse | undefined,
) {
  return useQuery({
    queryKey: oauthApplicationsQueryKeys.detail(idNum),
    queryFn: () => getOauthApplication(idNum),
    staleTime: OAUTH_STALE_TIME_MS,
    ...(seed ? { initialData: seed, initialDataUpdatedAt: Date.now() } : {}),
  });
}

function useOauthSeed(idNum: number) {
  const { initialOauthPayload } = useAppRouterInitial();
  return initialOauthPayload?.oauth_application?.id === idNum
    ? initialOauthPayload
    : undefined;
}

export const oauthAppsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/oauth_applications',
  component: function OAuthAppsIndexRoute() {
    const { translations, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.oauth_applications'));
    const [Component, setComponent] = useState<typeof OAuthAppManagement | null>(null);
    useEffect(() => {
      import('../../components/OAuthAppManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('oauth_apps.index.title')} />;
    }
    return <Component translations={translations} currentUser={currentUser} />;
  },
});

export const oauthAppDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/oauth_applications/$id',
  component: function OAuthAppDetailsRoute() {
    const params = oauthAppDetailsRoute.useParams();
    const { currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const { data } = useOauthApplicationRaw(idNum, useOauthSeed(idNum));
    useDocumentTitle(
      data?.oauth_application?.name ?? t('common.page_titles.oauth_applications'),
    );
    const [Component, setComponent] = useState<typeof OAuthAppDetails | null>(null);
    useEffect(() => {
      import('../../components/OAuthAppDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const payload = data;
    if (!Component || !payload?.oauth_application) {
      return <DetailsSkeleton label={t('oauth_apps.index.title')} />;
    }
    return (
      <Component
        oauthApplicationId={idNum}
        initialPayload={payload}
        currentUser={currentUser}
      />
    );
  },
});

export const oauthAppEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/oauth_applications/$id/edit',
  component: function OAuthAppEditRoute() {
    const params = oauthAppEditRoute.useParams();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const { data } = useOauthApplicationRaw(idNum, useOauthSeed(idNum));
    useDocumentTitle(
      data?.oauth_application
        ? t('oauth_apps.edit.title', { name: data.oauth_application.name })
        : t('common.page_titles.oauth_applications'),
    );
    const [Component, setComponent] = useState<typeof EditOAuthApp | null>(null);
    useEffect(() => {
      import('../../components/EditOAuthApp/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const application = data?.oauth_application;
    if (!Component || !application) {
      return <EditSkeleton label={t('oauth_apps.index.title')} />;
    }
    return <Component oauthApplicationId={idNum} initialApplication={application} />;
  },
});

export const oauthScopeRequestsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/oauth_scope_requests',
  component: function OAuthScopeRequestsIndexRoute() {
    const { translations, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.oauth_scope_requests'));
    const [Component, setComponent] = useState<typeof OAuthScopeRequests | null>(null);
    useEffect(() => {
      import('../../components/OAuthScopeRequests/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('scope_requests.index.title')} />;
    }
    return <Component translations={translations} currentUser={currentUser} />;
  },
});
